import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AlertCircle, CheckCircle, Clock, MessageCircle,
  Plus, Send, X, ChevronRight, Archive, Loader2,
} from 'lucide-react';
import { PageShell } from '@/components/layout/PageShell';
import { EmptyState } from '@/components/ui/EmptyState';
import { useSupportTickets, type SupportTicket, type TicketInput } from '@/hooks/useSupportTickets';
import { useChannels } from '@/hooks/useChannels';
import { validateName, validateEmail, validateDescription } from '@/lib/validators';
import { formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import { staggerContainer, staggerItem } from '@/lib/motion';

// ─── Labels ───────────────────────────────────────────────────────────────────

const STATUS_LABEL: Record<SupportTicket['status'], string>  = {
  open:     'Aberto',
  answered: 'Respondido',
  closed:   'Fechado',
};
const STATUS_CHIP: Record<SupportTicket['status'], string> = {
  open:     'chip-warning',
  answered: 'chip-positive',
  closed:   'chip-neutral',
};
const PRIORITY_CHIP: Record<SupportTicket['priority'], string> = {
  low:    'chip-neutral',
  normal: 'chip-info',
  high:   'chip-warning',
  urgent: 'chip-negative',
};
const PRIORITY_LABEL: Record<SupportTicket['priority'], string> = {
  low:    'Baixa',
  normal: 'Normal',
  high:   'Alta',
  urgent: 'Urgente',
};
const SOURCE_LABEL: Record<SupportTicket['source'], string> = {
  manual:        'Manual',
  mercado_livre: 'Mercado Livre',
  shopee:        'Shopee',
  whatsapp:      'WhatsApp',
  email:         'E-mail',
};

// ─── New Ticket Modal ─────────────────────────────────────────────────────────

const EMPTY: TicketInput = {
  customer_name: '', customer_email: '', subject: '',
  body: '', priority: 'normal', source: 'manual',
};

function NewTicketModal({
  channels,
  onSave,
  onClose,
}: {
  channels: { id: string; name: string }[];
  onSave: (d: TicketInput) => Promise<unknown>;
  onClose: () => void;
}) {
  const [form, setForm] = useState<TicketInput>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveErr, setSaveErr] = useState<string | null>(null);

  const set = (k: keyof TicketInput, v: unknown) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => { const n = { ...e }; delete n[k]; return n; });
  };

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const errs: Record<string, string> = {};
    const nameErr    = validateName(form.customer_name, { label: 'Nome do cliente', max: 120 });
    const emailErr   = validateEmail(form.customer_email ?? '');
    const subjectErr = validateName(form.subject, { label: 'Assunto', max: 200, strict: false });
    const bodyErr    = validateDescription(form.body, 5000);
    if (nameErr)    errs.customer_name  = nameErr;
    if (emailErr)   errs.customer_email = emailErr;
    if (subjectErr) errs.subject        = subjectErr;
    if (bodyErr)    errs.body           = bodyErr;
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    setSaveErr(null);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      setSaveErr(err instanceof Error ? err.message : 'Erro ao criar ticket. Tente novamente.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.18 }}
        className="card w-full max-w-lg p-6"
      >
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-semibold text-ink text-lg">Novo ticket</h2>
          <button onClick={onClose} className="btn-ghost !p-1.5"><X className="w-4 h-4" /></button>
        </div>

        {saveErr && (
          <div className="flex items-center justify-between gap-2 mb-4 rounded-lg bg-negative/10 border border-negative/20 px-3 py-2 text-sm text-negative">
            <span>{saveErr}</span>
            <button onClick={() => setSaveErr(null)} className="shrink-0"><X className="w-3.5 h-3.5" /></button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-ink-muted mb-1">Cliente *</label>
              <input className={cn('input w-full', errors.customer_name && 'border-negative')} value={form.customer_name}
                onChange={(e) => set('customer_name', e.target.value)} maxLength={120} />
              {errors.customer_name && <p className="text-xs text-negative mt-1">{errors.customer_name}</p>}
            </div>
            <div>
              <label className="block text-xs text-ink-muted mb-1">E-mail</label>
              <input type="email" className={cn('input w-full', errors.customer_email && 'border-negative')} value={form.customer_email ?? ''}
                onChange={(e) => set('customer_email', e.target.value)} maxLength={254} />
              {errors.customer_email && <p className="text-xs text-negative mt-1">{errors.customer_email}</p>}
            </div>
            <div>
              <label className="block text-xs text-ink-muted mb-1">Prioridade</label>
              <select className="input w-full" value={form.priority}
                onChange={(e) => set('priority', e.target.value)}>
                <option value="low">Baixa</option>
                <option value="normal">Normal</option>
                <option value="high">Alta</option>
                <option value="urgent">Urgente</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-ink-muted mb-1">Origem</label>
              <select className="input w-full" value={form.source}
                onChange={(e) => set('source', e.target.value)}>
                <option value="manual">Manual</option>
                <option value="mercado_livre">Mercado Livre</option>
                <option value="shopee">Shopee</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="email">E-mail</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-xs text-ink-muted mb-1">Canal</label>
              <select className="input w-full" value={form.channel_id ?? ''}
                onChange={(e) => set('channel_id', e.target.value || null)}>
                <option value="">Sem canal</option>
                {channels.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-xs text-ink-muted mb-1">Assunto *</label>
              <input className={cn('input w-full', errors.subject && 'border-negative')} value={form.subject}
                onChange={(e) => set('subject', e.target.value)} maxLength={200} />
              {errors.subject && <p className="text-xs text-negative mt-1">{errors.subject}</p>}
            </div>
            <div className="col-span-2">
              <label className="block text-xs text-ink-muted mb-1">Mensagem *</label>
              <textarea rows={4} className={cn('input w-full resize-none', errors.body && 'border-negative')}
                value={form.body} maxLength={5000}
                onChange={(e) => set('body', e.target.value)} />
              {errors.body && <p className="text-xs text-negative mt-1">{errors.body}</p>}
            </div>
          </div>
          <div className="flex gap-2 justify-end pt-1">
            <button type="button" onClick={onClose} disabled={saving} className="btn-secondary">Cancelar</button>
            <button type="submit" disabled={saving} className="btn-primary gap-2">
              {saving ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />Salvando…</> : 'Criar ticket'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

// ─── Ticket Detail Panel ──────────────────────────────────────────────────────

function TicketDetail({
  ticket,
  onReply,
  onStatus,
  onClose,
}: {
  ticket: SupportTicket;
  onReply: (id: string, reply: string) => Promise<void>;
  onStatus: (id: string, s: SupportTicket['status']) => Promise<void>;
  onClose: () => void;
}) {
  const [reply, setReply] = useState(ticket.reply ?? '');
  const [sending, setSending] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);

  async function handleReply(e: React.FormEvent) {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    await onReply(ticket.id, reply);
    setSending(false);
  }

  async function handleStatusClick(status: SupportTicket['status']) {
    setStatusSaving(true);
    await onStatus(ticket.id, status);
    setStatusSaving(false);
  }

  return (
    <motion.div
      key={ticket.id}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      transition={{ duration: 0.22 }}
      className="card p-5 flex flex-col gap-4"
    >
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={cn('chip text-[11px]', PRIORITY_CHIP[ticket.priority])}>
              {PRIORITY_LABEL[ticket.priority]}
            </span>
            <span className={cn('chip text-[11px]', STATUS_CHIP[ticket.status])}>
              {STATUS_LABEL[ticket.status]}
            </span>
            <span className="text-xs text-ink-muted">{SOURCE_LABEL[ticket.source]}</span>
          </div>
          <h3 className="font-semibold text-ink">{ticket.subject}</h3>
          <p className="text-xs text-ink-muted mt-0.5">
            {ticket.customer_name}
            {ticket.customer_email && ` · ${ticket.customer_email}`}
            {ticket.channel_name && ` · ${ticket.channel_name}`}
          </p>
          <p className="text-[11px] text-ink-soft mt-0.5">{formatDate(ticket.created_at)}</p>
        </div>
        <button onClick={onClose} className="btn-ghost !p-1.5 shrink-0">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Customer message */}
      <div className="rounded-xl bg-surface-subtle p-4">
        <p className="text-xs font-semibold text-ink-muted uppercase tracking-wider mb-2">Mensagem do cliente</p>
        <p className="text-sm text-ink whitespace-pre-wrap">{ticket.body}</p>
      </div>

      {/* Existing reply */}
      {ticket.reply && (
        <div className="rounded-xl bg-[#14a655]/5 border border-[#14a655]/20 p-4">
          <p className="text-xs font-semibold text-[#14a655] uppercase tracking-wider mb-2">Sua resposta</p>
          <p className="text-sm text-ink whitespace-pre-wrap">{ticket.reply}</p>
          {ticket.replied_at && (
            <p className="text-[11px] text-ink-soft mt-2">{formatDate(ticket.replied_at)}</p>
          )}
        </div>
      )}

      {/* Reply form */}
      {ticket.status !== 'closed' && (
        <form onSubmit={handleReply} className="flex flex-col gap-2">
          <label className="text-xs font-semibold text-ink-muted uppercase tracking-wider">
            {ticket.reply ? 'Atualizar resposta' : 'Responder'}
          </label>
          <textarea
            rows={3}
            className="input resize-none w-full"
            placeholder="Digite sua resposta…"
            value={reply}
            onChange={(e) => setReply(e.target.value)}
          />
          <div className="flex gap-2">
            <button type="submit" disabled={sending || !reply.trim()} className="btn-primary gap-2">
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              {sending ? 'Enviando…' : 'Responder'}
            </button>
            <button
              type="button"
              disabled={statusSaving}
              onClick={() => void handleStatusClick('closed')}
              className="btn-secondary gap-2"
            >
              {statusSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Archive className="w-3.5 h-3.5" />}
              Fechar ticket
            </button>
          </div>
        </form>
      )}

      {ticket.status === 'closed' && (
        <button
          disabled={statusSaving}
          onClick={() => void handleStatusClick('open')}
          className="btn-secondary self-start gap-2 text-sm"
        >
          {statusSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Clock className="w-3.5 h-3.5" />}
          Reabrir ticket
        </button>
      )}
    </motion.div>
  );
}

// ─── CustomerServicePage ──────────────────────────────────────────────────────

const TABS = [
  { key: '',         label: 'Todos' },
  { key: 'open',     label: 'Abertos' },
  { key: 'answered', label: 'Respondidos' },
  { key: 'closed',   label: 'Fechados' },
] as const;

export function CustomerServicePage() {
  const { tickets, loading, fetchError, addTicket, replyTicket, changeStatus } = useSupportTickets();
  const { channels } = useChannels();

  const [tab, setTab]             = useState<'' | SupportTicket['status']>('');
  const [priorityFilter, setPrio] = useState('');
  const [selected, setSelected]   = useState<SupportTicket | null>(null);
  const [modal, setModal]         = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const filtered = useMemo(() =>
    tickets.filter((t) => {
      if (tab && t.status !== tab) return false;
      if (priorityFilter && t.priority !== priorityFilter) return false;
      return true;
    }), [tickets, tab, priorityFilter]);

  const counts: Record<string, number> = useMemo(() => ({
    '':         tickets.length,
    open:       tickets.filter((t) => t.status === 'open').length,
    answered:   tickets.filter((t) => t.status === 'answered').length,
    closed:     tickets.filter((t) => t.status === 'closed').length,
  }), [tickets]);

  async function handleReply(id: string, reply: string): Promise<void> {
    try {
      await replyTicket(id, reply);
      if (selected?.id === id) {
        setSelected((prev) => prev
          ? { ...prev, reply, status: 'answered', replied_at: new Date().toISOString() }
          : prev
        );
      }
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Erro ao enviar resposta.');
    }
  }

  async function handleStatus(id: string, status: SupportTicket['status']): Promise<void> {
    try {
      await changeStatus(id, status);
      if (selected?.id === id) {
        setSelected((prev) => prev ? { ...prev, status } : prev);
      }
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Erro ao atualizar status.');
    }
  }

  const displayError = fetchError ?? actionError;

  return (
    <PageShell title="Atendimento" subtitle="Gerencie mensagens, perguntas e solicitações de clientes." maxWidth="xl">

      {/* Stats bar */}
      <motion.div
        className="grid grid-cols-3 gap-4 mb-6"
        variants={staggerContainer} initial="initial" animate="animate"
      >
        {[
          { label: 'Abertos',     value: counts.open,    icon: <AlertCircle className="w-4 h-4" />, color: 'text-warning' },
          { label: 'Respondidos', value: counts.answered, icon: <CheckCircle className="w-4 h-4" />, color: 'text-positive' },
          { label: 'Total',       value: counts[''],     icon: <MessageCircle className="w-4 h-4" />, color: 'text-ink' },
        ].map((s) => (
          <motion.div key={s.label} variants={staggerItem} className="card p-4">
            <div className="flex items-center gap-2 mb-1">
              <span className={s.color}>{s.icon}</span>
              <p className="text-xs text-ink-muted">{s.label}</p>
            </div>
            <p className={cn('text-2xl font-bold tabular-nums', s.color)}>{s.value}</p>
          </motion.div>
        ))}
      </motion.div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <div className="flex gap-1 rounded-xl bg-surface-subtle p-1">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => { setTab(t.key); setSelected(null); }}
              className={cn(
                'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5',
                tab === t.key
                  ? 'bg-surface-raised text-ink shadow-sm'
                  : 'text-ink-muted hover:text-ink'
              )}
            >
              {t.label}
              {(counts[t.key] ?? 0) > 0 && (
                <span className={cn(
                  'text-[11px] font-bold px-1.5 py-0.5 rounded-full',
                  tab === t.key ? 'bg-brand-100 text-brand-800' : 'bg-surface text-ink-muted'
                )}>
                  {counts[t.key]}
                </span>
              )}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <select className="input text-sm" value={priorityFilter} onChange={(e) => setPrio(e.target.value)}>
            <option value="">Todas prioridades</option>
            <option value="urgent">Urgente</option>
            <option value="high">Alta</option>
            <option value="normal">Normal</option>
            <option value="low">Baixa</option>
          </select>
          <button onClick={() => setModal(true)} className="btn-primary gap-2 shrink-0">
            <Plus className="w-4 h-4" />
            Novo ticket
          </button>
        </div>
      </div>

      {displayError && (
        <div className="flex items-center justify-between gap-2 mb-4 rounded-lg bg-negative/10 border border-negative/20 px-3 py-2 text-sm text-negative">
          <span>{displayError}</span>
          <button onClick={() => { setActionError(null); }} className="shrink-0"><X className="w-3.5 h-3.5" /></button>
        </div>
      )}

      {/* Two-column layout */}
      <div className={cn('grid gap-4', selected ? 'lg:grid-cols-2' : 'grid-cols-1')}>
        {/* Ticket list */}
        <div>
          {loading ? (
            <div className="space-y-2">
              {[1,2,3].map((i) => <div key={i} className="h-20 rounded-xl bg-surface-subtle animate-pulse" />)}
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState
              icon={MessageCircle}
              title={tickets.length === 0 ? 'Nenhum ticket ainda' : 'Nenhum resultado'}
              description={
                tickets.length === 0
                  ? 'Crie tickets manualmente ou integre canais para receber mensagens automaticamente.'
                  : 'Tente outro filtro.'
              }
            />
          ) : (
            <motion.div
              className="space-y-2"
              variants={staggerContainer} initial="initial" animate="animate"
            >
              {filtered.map((t) => (
                <motion.button
                  key={t.id}
                  variants={staggerItem}
                  onClick={() => setSelected((prev) => prev?.id === t.id ? null : t)}
                  className={cn(
                    'w-full text-left card p-4 hover:shadow-md transition-all flex items-start gap-3',
                    selected?.id === t.id && 'ring-2 ring-brand-400'
                  )}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={cn('chip text-[11px]', PRIORITY_CHIP[t.priority])}>
                        {PRIORITY_LABEL[t.priority]}
                      </span>
                      <span className={cn('chip text-[11px]', STATUS_CHIP[t.status])}>
                        {STATUS_LABEL[t.status]}
                      </span>
                      {t.channel_name && (
                        <span className="text-[11px] text-ink-soft">{t.channel_name}</span>
                      )}
                    </div>
                    <p className="font-semibold text-ink text-sm truncate">{t.subject}</p>
                    <p className="text-xs text-ink-muted mt-0.5 truncate">{t.customer_name}</p>
                    <p className="text-xs text-ink-soft mt-0.5 line-clamp-1">{t.body}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <p className="text-[11px] text-ink-soft">{formatDate(t.created_at)}</p>
                    <ChevronRight className={cn(
                      'w-4 h-4 text-ink-soft transition-transform',
                      selected?.id === t.id && 'rotate-90'
                    )} />
                  </div>
                </motion.button>
              ))}
            </motion.div>
          )}
        </div>

        {/* Detail panel */}
        <AnimatePresence>
          {selected && (
            <TicketDetail
              ticket={selected}
              onReply={handleReply}
              onStatus={handleStatus}
              onClose={() => setSelected(null)}
            />
          )}
        </AnimatePresence>
      </div>

      {/* New ticket modal */}
      <AnimatePresence>
        {modal && (
          <NewTicketModal
            channels={channels}
            onSave={addTicket}
            onClose={() => setModal(false)}
          />
        )}
      </AnimatePresence>
    </PageShell>
  );
}
